import { useServices } from '@/app/providers/ServiceContext';
import { useToast } from '@/app/providers';
import { Button } from '@/core/ui/components/Button';
import type { TenantPaymentMethod, TenantBillingInvoice } from '@yawi/types';
import { Check, CreditCard, Loader2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { formatCurrency } from '@yawi/infrastructure';

interface PayInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: TenantBillingInvoice;
  onSuccess: () => void;
  onAddPaymentMethod: () => void;
}

export const PayInvoiceModal: React.FC<PayInvoiceModalProps> = ({
  isOpen,
  onClose,
  invoice,
  onSuccess,
  onAddPaymentMethod,
}) => {
  const { billingRepo } = useServices();
  const toast = useToast();

  const [paymentMethods, setPaymentMethods] = useState<TenantPaymentMethod[]>([]);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPaymentMethods();
    }
  }, [isOpen]);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const methods = await billingRepo.getPaymentMethods();
      setPaymentMethods(methods);

      // Auto-select default method
      const defaultMethod = methods.find(m => m.isDefault);
      if (defaultMethod) {
        setSelectedMethodId(defaultMethod.id);
      } else if (methods.length > 0) {
        setSelectedMethodId(methods[0].id);
      }
    } catch {
      toast.error('Error al cargar los metodos de pago');
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async () => {
    if (!selectedMethodId) {
      toast.error('Selecciona un metodo de pago');
      return;
    }

    try {
      setPaying(true);
      await billingRepo.payInvoice(invoice.id, selectedMethodId);
      toast.success('Factura pagada correctamente');
      onSuccess();
      onClose();
    } catch {
      toast.error('Error al procesar el pago');
    } finally {
      setPaying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800">Pagar factura</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Invoice Summary */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-600">Factura</span>
              <span className="font-medium text-slate-800">{invoice.invoiceNumber}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-600">Total a pagar</span>
              <span className="text-xl font-bold text-slate-800">
                {formatCurrency(invoice.totalAmount, invoice.currency)}
              </span>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="mb-6">
            <h4 className="font-medium text-slate-800 mb-3">Selecciona un metodo de pago</h4>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
              </div>
            ) : paymentMethods.length === 0 ? (
              <div className="text-center py-8">
                <CreditCard className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">No tienes metodos de pago</p>
                <Button variant="secondary" onClick={onAddPaymentMethod}>
                  Agregar metodo de pago
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    onClick={() => setSelectedMethodId(method.id)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${
                      selectedMethodId === method.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-slate-200 hover:border-primary-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-6 bg-white rounded flex items-center justify-center border">
                        {method.cardBrand === 'visa' && <span className="text-blue-600 font-bold text-xs">VISA</span>}
                        {method.cardBrand === 'mastercard' && <span className="text-red-600 font-bold text-xs">MC</span>}
                        {method.cardBrand === 'amex' && <span className="text-blue-800 font-bold text-xs">AMEX</span>}
                        {!['visa', 'mastercard', 'amex'].includes(method.cardBrand || '') && (
                          <CreditCard className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-800">{method.label}</p>
                        {method.expMonth && method.expYear && (
                          <p className="text-xs text-slate-500">Vence {method.expMonth}/{method.expYear}</p>
                        )}
                      </div>
                    </div>
                    {selectedMethodId === method.id && (
                      <Check className="w-5 h-5 text-primary-500" />
                    )}
                  </button>
                ))}

                <button
                  onClick={onAddPaymentMethod}
                  className="w-full p-4 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  + Agregar nuevo metodo
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handlePay}
            disabled={!selectedMethodId || paying}
          >
            {paying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Procesando...
              </>
            ) : (
              `Pagar ${formatCurrency(invoice.totalAmount, invoice.currency)}`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PayInvoiceModal;
